import {AnyAction, isAnyOf} from '@reduxjs/toolkit';

import {storage} from '@edna/utils';

import {EStorageKeys} from 'src/constants';
import {TTenantUser, resetUser, setTenantId} from 'src/containers/User';
import {listenerMiddleware, rootApi} from 'src/models';

import {
  TCaptcha,
  TChangePasswordFormData,
  TSendOtpPayload,
  TSignInPayload,
  TSignInResponse,
} from './definitions';

const authApi = rootApi.injectEndpoints({
  overrideExisting: 'throw',
  endpoints: (builder) => ({
    getCaptcha: builder.query<TCaptcha, {domain?: string | null} | void>({
      query: ({domain} = {}) => ({
        method: 'GET',
        baseUrl: domain ? `${domain}/rest` : undefined,
        url: `/auth/captcha/gen`,
        meta: {isShowError: true},
      }),
    }),
    signIn: builder.mutation<TSignInResponse, TSignInPayload>({
      query: (data) => ({
        method: 'POST',
        url: `/auth/login`,
        data,
      }),
    }),
    signOut: builder.mutation<TEmptyObject, void>({
      query: () => ({
        method: 'POST',
        url: `/auth/logout`,
        meta: {
          isShowError: true,
        },
      }),
    }),
    changePassword: builder.mutation<void, Omit<TChangePasswordFormData, 'newPasswordRepeat'>>({
      query: (data) => ({
        method: 'POST',
        url: '/auth/password/change',
        data,
        meta: {
          isShowSuccess: true,
          successMessageKey: 'Profile:notification.changePasswordSuccess',
        },
      }),
    }),
    sendOtp: builder.mutation<TSignInResponse, TSendOtpPayload>({
      query: (data) => ({
        method: 'POST',
        url: '/auth/otp',
        data,
      }),
    }),
    resetOtp: builder.mutation<TSignInResponse, Pick<TSendOtpPayload, 'otpId'>>({
      query: (data) => ({
        method: 'POST',
        url: '/auth/otp-reset',
        data,
      }),
    }),
  }),
});

listenerMiddleware.startListening({
  matcher: isAnyOf(
    authApi.endpoints.signIn.matchFulfilled,
    authApi.endpoints.sendOtp.matchFulfilled,
  ),
  effect: ({payload, meta}: AnyAction, {dispatch}) => {
    if (meta.arg.originalArgs.username) {
      storage.set(EStorageKeys.USERNAME, meta.arg.originalArgs.username);
    }

    if (!payload.tenants) {
      return;
    }

    const tenant =
      payload.tenants.find(
        ({id}: TTenantUser) => id === Number(storage.get(EStorageKeys.TENANT_ID)),
      ) ?? payload.tenants[0];

    dispatch(setTenantId(tenant.id));
  },
});

listenerMiddleware.startListening({
  matcher: authApi.endpoints.signOut.matchFulfilled,
  effect: (_, {dispatch}) => {
    dispatch(resetUser());
  },
});

export const {
  useSignInMutation,
  useGetCaptchaQuery,
  useChangePasswordMutation,
  useSignOutMutation,
  useResetOtpMutation,
  useSendOtpMutation,
} = authApi;
